import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Mail, 
  Send, 
  RefreshCw, 
  Check, 
  X, 
  Clock, 
  BarChart3,
  Settings,
  Eye,
  RotateCw,
  Filter,
  Download,
  AlertCircle,
  TrendingUp,
  Key,
  Loader2
} from 'lucide-react';

export default function EmailAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('logs');
  const [loading, setLoading] = useState(true);
  
  // Email logs state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilter, setLogsFilter] = useState({ type: '', status: '', email: '' });
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  
  // Config state
  const [pricingConfig, setPricingConfig] = useState(null);
  const [promotionsConfig, setPromotionsConfig] = useState([]);
  
  // Email provider config state
  const [emailConfig, setEmailConfig] = useState({
    provider: 'sendgrid', // sendgrid, microsoft365
    // SendGrid
    sendgridApiKey: '',
    // Microsoft 365 Graph API
    microsoft365TenantId: '',
    microsoft365ClientId: '',
    microsoft365ClientSecret: '',
    // Common
    senderEmail: '',
    senderName: '',
    isConfigured: false
  });
  const [savingEmailConfig, setSavingEmailConfig] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  useEffect(() => {
    if (user?.role !== 'SuperAdmin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, logsPage, logsFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLogs(),
        fetchStats(),
        fetchTemplates(),
        fetchConfig(),
        fetchEmailConfig()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: logsPage.toString(),
        limit: '20'
      });
      if (logsFilter.type) params.append('type', logsFilter.type);
      if (logsFilter.status) params.append('status', logsFilter.status);
      if (logsFilter.email) params.append('email', logsFilter.email);
      
      const response = await api.get(`/admin/emails/logs?${params}`);
      setLogs(response.data.logs || []);
      setLogsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/emails/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/admin/emails/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const [pricingRes, promosRes] = await Promise.all([
        api.get('/admin/emails/config/pricing'),
        api.get('/admin/emails/config/promotions')
      ]);
      setPricingConfig(pricingRes.data);
      setPromotionsConfig(promosRes.data.promotions || []);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await api.get('/admin/emails/config/provider');
      if (response.data) {
        setEmailConfig(prev => ({
          ...prev,
          ...response.data,
          // Ne pas écraser les secrets avec des masques
          sendgridApiKey: response.data.sendgridApiKey || '',
          microsoft365ClientSecret: response.data.microsoft365ClientSecret || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    }
  };

  const saveEmailConfig = async () => {
    setSavingEmailConfig(true);
    try {
      await api.put('/admin/emails/config/provider', emailConfig);
      toast.success('Configuration email enregistrée');
      fetchEmailConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEmailConfig(false);
    }
  };

  const testEmailSend = async () => {
    if (!testEmailAddress) {
      toast.error('Veuillez entrer une adresse email de test');
      return;
    }
    setTestingEmail(true);
    try {
      await api.post('/admin/emails/test', { email: testEmailAddress });
      toast.success(`Email de test envoyé à ${testEmailAddress}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du test');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleResendEmail = async (emailId) => {
    try {
      await api.post(`/admin/emails/resend/${emailId}`);
      toast.success('Email en cours de renvoi');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du renvoi');
    }
  };

  const handleToggleTemplate = async (templateId) => {
    try {
      const response = await api.put(`/admin/emails/templates/${templateId}/toggle`);
      setTemplates(templates.map(t => 
        t.id === templateId ? { ...t, enabled: response.data.enabled } : t
      ));
      toast.success('Template mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSavePromotions = async () => {
    try {
      await api.post('/admin/emails/config/promotions', { promotions: promotionsConfig });
      toast.success('Promotions mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-100 text-green-800"><Check className="w-3 h-3 mr-1" />Envoyé</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><X className="w-3 h-3 mr-1" />Échoué</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      confirmation: { label: 'Confirmation', color: 'bg-blue-100 text-blue-800' },
      welcome: { label: 'Bienvenue', color: 'bg-green-100 text-green-800' },
      password_reset: { label: 'Reset MDP', color: 'bg-orange-100 text-orange-800' },
      password_changed: { label: 'MDP Modifié', color: 'bg-purple-100 text-purple-800' },
      subscription: { label: 'Abonnement', color: 'bg-indigo-100 text-indigo-800' },
      invoice: { label: 'Facture', color: 'bg-cyan-100 text-cyan-800' },
      trial_ending: { label: 'Fin essai', color: 'bg-amber-100 text-amber-800' },
      payment_failed: { label: 'Paiement échoué', color: 'bg-red-100 text-red-800' },
      promotion: { label: 'Promotion', color: 'bg-pink-100 text-pink-800' }
    };
    const config = types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading && !stats) {
    return (
      <DashboardLayout title="Administration Emails">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Administration Emails">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Gestion des Emails</h1>
            <p className="text-slate-500">Logs, templates et configuration des emails</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total envoyés</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  </div>
                  <Mail className="w-8 h-8 text-webtech-blue opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Envoyés</p>
                    <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
                  </div>
                  <Check className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Échoués</p>
                    <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Taux de livraison</p>
                    <p className="text-2xl font-bold text-webtech-blue">{stats.deliveryRate}%</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-webtech-blue opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Logs d'envoi
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="promotions" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Promotions
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configuration
            </TabsTrigger>
          </TabsList>

          {/* Logs Tab */}
          <TabsContent value="logs" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Historique des envois</CardTitle>
                <CardDescription>Tous les emails envoyés via SendGrid</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Filtrer par email..."
                      value={logsFilter.email}
                      onChange={(e) => setLogsFilter({ ...logsFilter, email: e.target.value })}
                    />
                  </div>
                  <Select
                    value={logsFilter.type || "all"}
                    onValueChange={(value) => setLogsFilter({ ...logsFilter, type: value === "all" ? "" : value })}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      <SelectItem value="confirmation">Confirmation</SelectItem>
                      <SelectItem value="welcome">Bienvenue</SelectItem>
                      <SelectItem value="password_reset">Reset MDP</SelectItem>
                      <SelectItem value="subscription">Abonnement</SelectItem>
                      <SelectItem value="promotion">Promotion</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={logsFilter.status || "all"}
                    onValueChange={(value) => setLogsFilter({ ...logsFilter, status: value === "all" ? "" : value })}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="sent">Envoyé</SelectItem>
                      <SelectItem value="failed">Échoué</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Logs Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Destinataire</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Sujet</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Statut</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                            Aucun email trouvé
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          <tr key={log.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm text-slate-900">{log.to}</td>
                            <td className="px-4 py-3">{getTypeBadge(log.type)}</td>
                            <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{log.subject}</td>
                            <td className="px-4 py-3">{getStatusBadge(log.status)}</td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {new Date(log.createdAt).toLocaleString('fr-FR')}
                            </td>
                            <td className="px-4 py-3">
                              {log.status === 'failed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResendEmail(log.id)}
                                >
                                  <RotateCw className="w-3 h-3 mr-1" />
                                  Renvoyer
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {logsTotal > 20 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                      disabled={logsPage === 1}
                    >
                      Précédent
                    </Button>
                    <span className="px-4 py-2 text-sm text-slate-600">
                      Page {logsPage} / {Math.ceil(logsTotal / 20)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLogsPage(p => p + 1)}
                      disabled={logsPage >= Math.ceil(logsTotal / 20)}
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Templates d'emails</CardTitle>
                <CardDescription>Activer ou désactiver les types d'emails</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <Mail className={`w-5 h-5 ${template.enabled ? 'text-webtech-blue' : 'text-slate-400'}`} />
                        <div>
                          <p className="font-medium text-slate-900">{template.name}</p>
                          <p className="text-sm text-slate-500">{template.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={template.enabled}
                        onCheckedChange={() => handleToggleTemplate(template.id)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Configuration des promotions</CardTitle>
                    <CardDescription>Gérer les offres promotionnelles</CardDescription>
                  </div>
                  <Button onClick={handleSavePromotions}>
                    Sauvegarder
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {promotionsConfig.map((promo, index) => (
                    <div key={promo.id} className="p-4 border rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-900">Promotion {index + 1}</h4>
                        <Switch
                          checked={promo.active}
                          onCheckedChange={(checked) => {
                            const updated = [...promotionsConfig];
                            updated[index] = { ...promo, active: checked };
                            setPromotionsConfig(updated);
                          }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Code promo</Label>
                          <Input
                            value={promo.code}
                            onChange={(e) => {
                              const updated = [...promotionsConfig];
                              updated[index] = { ...promo, code: e.target.value };
                              setPromotionsConfig(updated);
                            }}
                          />
                        </div>
                        <div>
                          <Label>Réduction (%)</Label>
                          <Input
                            type="number"
                            value={promo.discount}
                            onChange={(e) => {
                              const updated = [...promotionsConfig];
                              updated[index] = { ...promo, discount: parseInt(e.target.value) };
                              setPromotionsConfig(updated);
                            }}
                          />
                        </div>
                        <div>
                          <Label>Validité (heures)</Label>
                          <Input
                            type="number"
                            value={promo.validityHours || ''}
                            placeholder="Illimitée"
                            onChange={(e) => {
                              const updated = [...promotionsConfig];
                              updated[index] = { ...promo, validityHours: e.target.value ? parseInt(e.target.value) : null };
                              setPromotionsConfig(updated);
                            }}
                          />
                        </div>
                        <div>
                          <Label>Score maturité min</Label>
                          <Input
                            type="number"
                            value={promo.minMaturityScore || 0}
                            onChange={(e) => {
                              const updated = [...promotionsConfig];
                              updated[index] = { ...promo, minMaturityScore: parseInt(e.target.value) };
                              setPromotionsConfig(updated);
                            }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Message affiché</Label>
                        <Input
                          value={promo.message}
                          onChange={(e) => {
                            const updated = [...promotionsConfig];
                            updated[index] = { ...promo, message: e.target.value };
                            setPromotionsConfig(updated);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Configuration Tab */}
          <TabsContent value="config" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configuration du fournisseur d'emails
                </CardTitle>
                <CardDescription>
                  Configurez le service d'envoi d'emails (SendGrid ou Microsoft 365)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Provider Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Fournisseur d'emails</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        emailConfig.provider === 'sendgrid' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setEmailConfig({ ...emailConfig, provider: 'sendgrid' })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                          SG
                        </div>
                        <div>
                          <p className="font-medium">SendGrid</p>
                          <p className="text-sm text-slate-500">Service email cloud</p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        emailConfig.provider === 'microsoft365' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setEmailConfig({ ...emailConfig, provider: 'microsoft365' })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0078d4] rounded-lg flex items-center justify-center text-white font-bold text-xs">
                          M365
                        </div>
                        <div>
                          <p className="font-medium">Microsoft 365</p>
                          <p className="text-sm text-slate-500">Graph API</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SendGrid Config */}
                {emailConfig.provider === 'sendgrid' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Configuration SendGrid
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sendgrid-key">Clé API SendGrid</Label>
                        <Input
                          id="sendgrid-key"
                          type="password"
                          placeholder="SG.xxxxx..."
                          value={emailConfig.sendgridApiKey}
                          onChange={(e) => setEmailConfig({ ...emailConfig, sendgridApiKey: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sender-email">Email expéditeur</Label>
                        <Input
                          id="sender-email"
                          type="email"
                          placeholder="noreply@votreentreprise.com"
                          value={emailConfig.senderEmail}
                          onChange={(e) => setEmailConfig({ ...emailConfig, senderEmail: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Microsoft 365 Config */}
                {emailConfig.provider === 'microsoft365' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Configuration Microsoft 365 Graph API
                    </h4>
                    <p className="text-sm text-slate-600">
                      Pour configurer Microsoft Graph API, vous devez créer une application dans Azure AD.
                      <a 
                        href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline ml-1"
                      >
                        Accéder au portail Azure →
                      </a>
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="m365-tenant">Tenant ID (Directory ID)</Label>
                        <Input
                          id="m365-tenant"
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={emailConfig.microsoft365TenantId}
                          onChange={(e) => setEmailConfig({ ...emailConfig, microsoft365TenantId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="m365-client">Client ID (Application ID)</Label>
                        <Input
                          id="m365-client"
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={emailConfig.microsoft365ClientId}
                          onChange={(e) => setEmailConfig({ ...emailConfig, microsoft365ClientId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="m365-secret">Client Secret</Label>
                        <Input
                          id="m365-secret"
                          type="password"
                          placeholder="Votre secret client..."
                          value={emailConfig.microsoft365ClientSecret}
                          onChange={(e) => setEmailConfig({ ...emailConfig, microsoft365ClientSecret: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="m365-sender">Email expéditeur (Microsoft 365)</Label>
                        <Input
                          id="m365-sender"
                          type="email"
                          placeholder="notifications@votreentreprise.com"
                          value={emailConfig.senderEmail}
                          onChange={(e) => setEmailConfig({ ...emailConfig, senderEmail: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <strong>Permissions requises :</strong> L'application Azure AD doit avoir la permission 
                        <code className="bg-amber-100 px-1 mx-1 rounded">Mail.Send</code> 
                        (Application permission, pas Delegated).
                      </p>
                    </div>
                  </div>
                )}

                {/* Common Config */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium">Paramètres généraux</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sender-name">Nom de l'expéditeur</Label>
                      <Input
                        id="sender-name"
                        placeholder="HR Engine"
                        value={emailConfig.senderName}
                        onChange={(e) => setEmailConfig({ ...emailConfig, senderName: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    {emailConfig.isConfigured ? (
                      <>
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="text-green-600">Configuration active</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-amber-600">Non configuré</span>
                      </>
                    )}
                  </div>
                  <Button 
                    onClick={saveEmailConfig} 
                    disabled={savingEmailConfig}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingEmailConfig ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer la configuration
                  </Button>
                </div>

                {/* Test Email */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Tester la configuration</h4>
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      placeholder="votre@email.com"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      className="max-w-xs"
                    />
                    <Button 
                      variant="outline" 
                      onClick={testEmailSend}
                      disabled={testingEmail || !testEmailAddress}
                    >
                      {testingEmail ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Envoyer un test
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
